

<script setup>
import { nodeViewProps, NodeViewWrapper } from '@tiptap/vue-3'
import { computed, ref, unref, watchEffect } from 'vue'
import { IMAGE_MAX_SIZE, IMAGE_MIN_SIZE, IMAGE_THROTTLE_WAIT_TIME } from '@/tiptap/constants/define'
import { clamp, isNumber, throttle } from '@/tiptap/utils/utils'

const props = defineProps({
  ...nodeViewProps,
  selected: { type: Boolean, required: true }
})

const ResizeDirection = {
  TOP_LEFT: 'tl',
  TOP_RIGHT: 'tr',
  BOTTOM_LEFT: 'bl',
  BOTTOM_RIGHT: 'br'
}

const maxSize = ref({ width: IMAGE_MAX_SIZE, height: IMAGE_MAX_SIZE })
const originalSize = ref({ width: 0, height: 0 })
const resizeDirections = ref(Object.values(ResizeDirection))
const resizing = ref(false)
const resizerState = ref({ x: 0, y: 0, w: 0, h: 0, dir: '' })

const imgAttrs = computed(() => {
  const { src, alt, width: w, height: h } = props.node.attrs
  const width = isNumber(w) ? `${w}px` : w
  const height = isNumber(h) ? `${h}px` : h
  return { src, alt, style: { width, height } }
})

const display = computed(() => props.node.attrs.display || undefined)
const lockAspectRatio = computed(() => props.node.attrs.lockAspectRatio ?? true)
const imageViewClass = computed(() =>
  typeof unref(display) === 'string'
    ? ['image-view', `image-view--${unref(display)}`]
    : ['image-view']
)
const imageMaxStyle = computed(() => {
  const { style: { width } } = unref(imgAttrs)
  return { width: width === '100%' ? width : undefined }
})

function onImageLoad(e) {
  originalSize.value = { width: e.target.width, height: e.target.height }
}

function selectImage() {
  const { editor, getPos } = props
  const pos = getPos()
  if (pos) editor.commands.setNodeSelection(pos)
}

const getMaxSize = throttle(() => {
  const { editor } = props
  const { width } = getComputedStyle(editor.view.dom)
  maxSize.value.width = Number.parseInt(width, 10)
}, IMAGE_THROTTLE_WAIT_TIME)

function onMouseDown(e, dir) {
  e.preventDefault()
  e.stopPropagation()

  resizerState.value.x = e.clientX
  resizerState.value.y = e.clientY

  const originalWidth = unref(originalSize).width
  const originalHeight = unref(originalSize).height
  const aspectRatio = originalWidth / originalHeight

  let width = Number(props.node.attrs.width)
  let height = Number(props.node.attrs.height)
  const maxWidth = unref(maxSize).width

  if (width && !height) {
    width = width > maxWidth ? maxWidth : width
    height = Math.round(width / aspectRatio)
  } else if (height && !width) {
    width = Math.round(height * aspectRatio)
    width = width > maxWidth ? maxWidth : width
  } else if (!width && !height) {
    width = originalWidth > maxWidth ? maxWidth : originalWidth
    height = Math.round(width / aspectRatio)
  } else {
    width = width > maxWidth ? maxWidth : width
  }

  resizerState.value.w = width
  resizerState.value.h = height
  resizerState.value.dir = dir
  resizing.value = true
  onEvents()
}

const onMouseMove = throttle((e) => {
  e.preventDefault()
  e.stopPropagation()
  if (!unref(resizing)) return

  const { x, y, w, h, dir } = unref(resizerState)
  const dx = (e.clientX - x) * (/l/.test(dir) ? -1 : 1)
  const dy = (e.clientY - y) * (/t/.test(dir) ? -1 : 1)

  const width = clamp(w + dx, IMAGE_MIN_SIZE, unref(maxSize).width)
  const height = unref(lockAspectRatio) ? null : Math.max(h + dy, IMAGE_MIN_SIZE)

  props.updateAttributes({ width, height })
}, IMAGE_THROTTLE_WAIT_TIME)

function onMouseUp(e) {
  e.preventDefault()
  e.stopPropagation()
  if (!unref(resizing)) return
  resizing.value = false
  resizerState.value = { x: 0, y: 0, w: 0, h: 0, dir: '' }
  offEvents()
  selectImage()
}

function onEvents() {
  document.addEventListener('mousemove', onMouseMove, true)
  document.addEventListener('mouseup', onMouseUp, true)
}

function offEvents() {
  document.removeEventListener('mousemove', onMouseMove, true)
  document.removeEventListener('mouseup', onMouseUp, true)
}

const resizeOb = new ResizeObserver(() => getMaxSize())
watchEffect((cleanup) => {
  resizeOb.observe(props.editor.view.dom)
  cleanup(() => resizeOb.disconnect())
})
</script>

<template>
  <NodeViewWrapper as="span" :class="imageViewClass" :style="imageMaxStyle">
    <div
      draggable="true"
      data-drag-handle
      :class="{
        'image-view__body--focused': selected,
        'image-view__body--resizing': resizing
      }"
      class="image-view__body"
      :style="imageMaxStyle"
    >
      <img
        :src="imgAttrs.src"
        :alt="imgAttrs.alt"
        :style="imgAttrs.style"
        class="image-view__body__image"
        @load="onImageLoad"
        @click="selectImage"
      />

      <div v-if="editor.view.editable" v-show="selected || resizing" class="image-resizer">
        <span
          v-for="direction in resizeDirections"
          :key="direction"
          :class="`image-resizer__handler--${direction}`"
          class="image-resizer__handler"
          @mousedown="onMouseDown($event, direction)"
        ></span>
      </div>
    </div>
  </NodeViewWrapper>
</template>